import { getKeys } from 'shared/lib/object';
import { isObject } from 'shared/lib/type-guards';

import { monthsNameToNumber } from '../constants';
import type { CustomHoliday, FormattedHolidays, Holiday, HolidaysToSubmit } from '../model';

const formatDate = (toFormat: number | string): string => {
  if (Number(toFormat) < 10) {
    return `0${toFormat}`;
  }

  return toFormat.toString();
};

const getFormattedDate = (year: string | number, month?: number | string, day?: number | string) => {
  let name = `${year}`;

  if (month) {
    const currMonth = typeof month === 'number' ? month : monthsNameToNumber[month];

    name += `-${formatDate(currMonth)}`;
  }

  if (day) {
    name += `-${formatDate(day)}`;
  }

  return name;
};

export const getCheckboxName = (year: string | number, month?: number | string, day?: number | string) => {
  return `checkbox-${getFormattedDate(year, month, day)}`;
};

export const formatHolidayData = (holidays: Holiday[]): FormattedHolidays => {
  return holidays.reduce((acc: FormattedHolidays, item: Holiday) => {
    const year = item.year;
    const month = item.month;
    const day = item.day;

    if (!isObject(acc[year])) {
      acc[year] = {};
    }

    if (!isObject(acc[year][month])) {
      acc[year][month] = {};
    }

    if (isObject(acc[year][month])) {
      acc[year][month][day] = item;
    }

    return acc;
  }, {});
};

export const flatHolidays = (holidays: FormattedHolidays) => {
  const flatHolidays: (Holiday | CustomHoliday)[] = [];

  getKeys(holidays).forEach((year) =>
    getKeys(holidays[year]).forEach((month) =>
      getKeys(holidays[year][month]).forEach((day) => {
        flatHolidays.push(holidays[year][month][day]);
      })
    )
  );

  return flatHolidays;
};

export const getNumOfSelectedHolidaysInYear = (holiday: FormattedHolidays, year: string): number =>
  getKeys(holiday[year]).reduce((acc, month) => {
    return acc + getKeys(holiday[year][month]).filter((day) => holiday[year][month][day].isChecked).length;
  }, 0);

export const getMinDate = () => {
  let now = new Date();

  now.setDate(now.getDate() + 14);
  let day = 0; // Sunday

  while (now.getDay() !== day) {
    now.setDate(now.getDate() + 1);
  }

  now.setDate(now.getDate() + 1);
  now.setUTCHours(0, 0, 0, 0);

  return now;
};

export const isAllMonthChecked = (holidays: FormattedHolidays, year: string, month: string): boolean => {
  return getKeys(holidays[year][month]).every((day) => {
    return holidays[year][month][day].isChecked;
  });
};

export const isAllMonthDisabled = (holidays: FormattedHolidays, year: string, month: string): boolean => {
  return getKeys(holidays[year][month]).every((day) => {
    if (!holidays[year][month][day].isAvailable) {
      return true;
    }

    return false;
  });
};

export const isAllYearChecked = (holidays: FormattedHolidays, year: string): boolean => {
  return getKeys(holidays[year]).every((month) => {
    return isAllMonthChecked(holidays, year, month);
  });
};

export const holidaysToSubmit = (holidays: FormattedHolidays): HolidaysToSubmit => {
  const flattedHolidays = flatHolidays(holidays);

  const existedHolidays = flattedHolidays
    .filter((item) => item.id)
    .map((item) => {
      return {
        id: item.id,
        isChecked: item.isChecked,
      };
    });

  const newCustomHolidays = flattedHolidays
    .filter((item) => !item.id)
    .map((item) => {
      const { year, month, day } = item;

      return { date: getFormattedDate(year, month, day), isChecked: item.isChecked };
    });

  return {
    holidays: existedHolidays,
    newCustomHolidays,
  };
};
